import React from 'react';

interface UsageWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: () => void;
  onExit: () => void;
  type: 'warning' | 'exceeded';
  hasActiveMembership: boolean;
  remainingMinutes?: number;
}

const UsageWarningModal: React.FC<UsageWarningModalProps> = ({
  isOpen,
  onClose,
  onContinue,
  onExit,
  type,
  hasActiveMembership,
  remainingMinutes = 0,
}) => {
  if (!isOpen) return null;

  const isWarning = type === 'warning';
  const isExceeded = type === 'exceeded';

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
      <div className='bg-white rounded-2xl shadow-xl max-w-md w-full p-6'>
        <div className='text-center mb-6'>
          <div
            className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              isWarning
                ? 'bg-yellow-100'
                : hasActiveMembership
                ? 'bg-orange-100'
                : 'bg-red-100'
            }`}
          >
            {isWarning ? (
              <svg
                className='w-8 h-8 text-yellow-600'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                  clipRule='evenodd'
                />
              </svg>
            ) : (
              <svg
                className={`w-8 h-8 ${
                  hasActiveMembership ? 'text-orange-600' : 'text-red-600'
                }`}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                  clipRule='evenodd'
                />
              </svg>
            )}
          </div>

          <h3 className='text-2xl font-bold text-gray-900 mb-2'>
            {isWarning
              ? 'Usage Warning'
              : hasActiveMembership
              ? 'Usage Exceeded'
              : 'Usage Limit Reached'}
          </h3>

          <div className='text-gray-600'>
            {isWarning ? (
              <div className='space-y-2'>
                <p>
                  You have{' '}
                  <span className='font-semibold text-yellow-600'>
                    {remainingMinutes} minutes
                  </span>{' '}
                  of usage remaining this month.
                </p>
                <p>Would you like to continue the interview?</p>
              </div>
            ) : hasActiveMembership ? (
              <div className='space-y-2'>
                <p>You've used all your monthly minutes.</p>
                <p>
                  Continuing will use additional time and will be charged based
                  on your current plan at the next billing cycle.
                </p>
              </div>
            ) : (
              <div className='space-y-2'>
                <p>You've used all your free minutes for this month.</p>
                <p>
                  To continue practicing, please upgrade your plan or wait for
                  your usage to reset next month.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex space-x-3'>
          <button
            onClick={onExit}
            className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
          >
            Exit Interview
          </button>

          {(isWarning || hasActiveMembership) && (
            <button
              onClick={onContinue}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-white transition-colors ${
                isWarning
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              Continue Interview
            </button>
          )}
        </div>

        {/* Additional Info */}
        {isExceeded && hasActiveMembership && (
          <div className='mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200'>
            <div className='text-sm text-blue-700'>
              <p className='font-medium mb-1'>Billing Information</p>
              <p>
                Additional minutes will be calculated and charged at your next
                billing cycle based on your current plan's overage rate.
              </p>
            </div>
          </div>
        )}

        {!hasActiveMembership && isExceeded && (
          <div className='mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200'>
            <div className='text-sm text-gray-600 text-center'>
              <button
                onClick={() => {
                  // Navigate to subscription page
                  window.location.href = '/profile/subscription';
                }}
                className='text-blue-600 hover:text-blue-700 font-medium underline'
              >
                Upgrade your plan →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageWarningModal;
